import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';

import { recordFlight } from '@/lib/flight';
import { cn } from '@/lib/utils';
import { liftable, pressable, prefersReducedMotion, SPRING } from '@/lib/motion';

/** TMDB poster widths the CDN serves (image.tmdb.org/t/p/<size>/…). */
const TMDB_POSTER_WIDTHS = [92, 154, 185, 342, 500] as const;
const TMDB_SIZE_SEGMENT = /(\/t\/p\/)(w\d+|original)(\/)/;

/**
 * `<img sizes>` presets for the poster slots (ui-ux.md §6). Each is the slot's
 * rendered CSS width; the browser picks the TMDB width from `srcSet`.
 * `Poster` prefixes `auto, ` (the img is `loading="lazy"`), so engines that
 * support `sizes="auto"` use the real layout width and the rest use these.
 */
export const POSTER_SIZES = {
  /** List / feed / palette thumbs, 22–48px. */
  thumb: '48px',
  /** The horizontal rail (`PosterRow`): 140px → 168px from `md`. */
  rail: '(min-width: 768px) 168px, 140px',
  /** A rail of `PerspectiveCover` cases: 140px → 168px (`md`) → 184px (`lg`). */
  case: '(min-width: 1024px) 184px, (min-width: 768px) 168px, 140px',
  /** A hero case (Tonight's Up next): 200px → 220px (`md`) → 260px (`lg`). */
  hero: '(min-width: 1024px) 260px, (min-width: 768px) 220px, 200px',
  /** The cover wall (`PosterGrid`): `minmax(140|180px, 1fr)` tracks. */
  grid: '(min-width: 768px) 240px, (min-width: 480px) 200px, 50vw',
  /** No hint: large enough for a detail-page cover, never above w500. */
  default: '342px',
} as const;

/**
 * `srcSet` over the TMDB widths when `url` is a TMDB CDN path (any size
 * segment, `original` included); `undefined` for anything else — a mirrored
 * CloudFront key is stored at one size, so it keeps a single `src`.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function posterSrcSet(url: string | undefined): string | undefined {
  if (!url || !TMDB_SIZE_SEGMENT.test(url)) return undefined;
  return TMDB_POSTER_WIDTHS.map(
    (w) => `${url.replace(TMDB_SIZE_SEGMENT, `$1w${w}$3`)} ${w}w`,
  ).join(', ');
}

export interface PosterProps {
  /** Title id — the shared-element key is always `poster-${id}`. */
  id: string;
  /** Title name — the img alt and the error fallback text. */
  name: string;
  /** The title's two palette colours (placeholder + error fallback). */
  palette: [string, string];
  imageUrl?: string;
  imageFallbackUrl?: string;
  /**
   * Participate in the shared-element transition into /show/:id
   * (ui-ux.md §4). Off under prefers-reduced-motion and for throwaway thumbs.
   */
  layout?: boolean;
  /**
   * The slot's rendered width as an `<img sizes>` value — a `POSTER_SIZES`
   * preset or any sizes list. Only used when the image is a TMDB path
   * (`srcSet`); defaults to `POSTER_SIZES.default`.
   */
  sizes?: string;
  /** Set the title in the palette on error; small thumbs just stay palette. */
  fallbackTitle?: boolean;
  /**
   * Above-the-fold cover (the first row of a grid — ui-ux.md §7 Performance
   * budgets): load eagerly with `fetchpriority="high"`. Default lazy.
   */
  priority?: boolean;
  /**
   * Hover lift (`liftable()`). Off inside a `PerspectiveCover`, whose turn is
   * the hover — the poster then keeps only the press (`pressable()`).
   */
  lift?: boolean;
  /** Status marks etc., rendered above the artwork. */
  children?: ReactNode;
  className?: string;
}

/**
 * The shared 2:3 cover (ui-ux.md §6): a palette-colour placeholder until the
 * image decodes (`loading="lazy"`, `decoding="async"`), primary → fallback
 * origin chain, a TMDB `srcSet` picked by the slot's `sizes` hint, and on
 * error the palette plus the title — never a broken-image icon. Press/hover
 * motion comes from src/lib/motion.ts.
 */
export function Poster({
  id,
  name,
  palette,
  imageUrl,
  imageFallbackUrl,
  layout = true,
  sizes = POSTER_SIZES.default,
  fallbackTitle = true,
  priority = false,
  lift = true,
  children,
  className,
}: PosterProps) {
  const [src, setSrc] = useState<string | undefined>(imageUrl);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>(imageUrl ? 'loading' : 'error');

  const imgRef = useRef<HTMLImageElement | null>(null);
  const shownUrl = useRef(imageUrl);

  useEffect(() => {
    // Reset only when the URL really changes. A cached image can fire `load`
    // before this effect runs; resetting unconditionally left it stuck at
    // opacity 0. An image that is already decoded is ready immediately.
    if (shownUrl.current !== imageUrl) {
      shownUrl.current = imageUrl;
      setSrc(imageUrl);
      setPhase(imageUrl ? 'loading' : 'error');
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setPhase('ready');
  }, [imageUrl]);

  const handleError = () => {
    if (src && imageFallbackUrl && src !== imageFallbackUrl) {
      setSrc(imageFallbackUrl);
      return;
    }
    setPhase('error');
  };

  // Right-size TMDB images per slot; a mirrored key (single size) keeps `src`.
  const srcSet = posterSrcSet(src);

  const layoutId = layout && !prefersReducedMotion() ? `poster-${id}` : undefined;

  // A layoutId without an explicit transition falls back to framer-motion's
  // default layout spring, not the documented shared-element feel
  // (ui-ux.md §4). Scope SPRING.shared to just the `layout` sub-key so it
  // only governs the shared-element morph — press/hover still use
  // liftable()'s SPRING.press for their scale animation (a flat
  // `transition={SPRING.shared}` here would silently slow down hover/press
  // feedback on every poster in the app, since `layoutId` is set by default
  // on all of them, not only the ShowDetail hero that actually morphs).
  const press = lift ? liftable() : pressable();
  return (
    <motion.div
      layoutId={layoutId}
      // framer-motion makes any element with a tap gesture focusable
      // (tabIndex=0). A poster always sits inside the real link/button, so
      // keep it out of the tab order — otherwise every cover is an extra,
      // unlabeled tab stop.
      tabIndex={-1}
      // Where this cover was when activated: the title page's hero flies from it.
      onPointerDownCapture={(e) => recordFlight(id, e.currentTarget, palette)}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') recordFlight(id, e.currentTarget, palette);
      }}
      {...press}
      {...(layoutId
        ? { transition: { ...(press.transition as object), layout: SPRING.shared } }
        : null)}
      className={cn('relative aspect-[2/3] overflow-hidden rounded-sm', className)}
      style={{ background: `linear-gradient(160deg, ${palette[0]} 0%, ${palette[1]} 100%)` }}
    >
      {src && phase !== 'error' && (
        <img
          ref={imgRef}
          // `sizes="auto"` is only valid on a lazy image.
          sizes={srcSet ? (priority ? sizes : `auto, ${sizes}`) : undefined}
          srcSet={srcSet}
          src={src}
          alt={name}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          decoding="async"
          onLoad={() => setPhase('ready')}
          onError={handleError}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-150',
            phase === 'ready' ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      {phase === 'error' && fallbackTitle && (
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <p className="line-clamp-4 text-center text-headline text-on-image">{name}</p>
        </div>
      )}
      {children}
    </motion.div>
  );
}
